/**
 * The Records tab: golden records, what is still waiting, and what needs a person.
 *
 * Three views over the same pipeline. Golden records are the output; staged
 * records show what has not made it through and why; review is the queue of
 * pairs the match rules could not decide alone.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {EmptyState, SkeletonRows} from '@/shared/components'
import { Chip, SearchBox, SubTabs, type ChipTone } from '../components'
import { CountsStrip } from './CountsStrip'
import { ReviewQueue } from './ReviewQueue'
import { StagedRecords } from './StagedRecords'
import { useRecords } from '../hooks'
import type { GraphModel } from '../types/brain.types'

type View = 'golden' | 'staged' | 'review'

const STATUS_TONE: Record<string, ChipTone> = {
  ACTIVE: 'ok',
  REVIEW: 'warn',
  FLAGGED: 'err',
  MERGED_AWAY: 'plain',
}

export function RecordsTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const navigate = useNavigate()
  const [view, setView] = useState<View>('golden')
  // Every type to begin with: defaulting to the first one shows an empty table
  // whenever that type happens to have no records, which reads as "no records"
  // when the graph is full of them.
  const [nodeTypeKey, setNodeTypeKey] = useState<string>('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const records = useRecords(graphId, {
    node_type_key: nodeTypeKey || undefined,
    q: query || undefined,
    page,
  })

  const columns =
    model.node_types.find((n) => n.key === nodeTypeKey)?.properties.slice(0, 5) ?? []

  return (
    <div className="brain-panel">
      <div className="brain-colstack">
        <div className="cp-toolbar">
          <h1>Records</h1>
          <Chip>golden · deduplicated</Chip>
          <span className="cp-spacer" />
          <div className="sub2">
            What the pipeline produced, after matching and survivorship. Open one for its 360°
            view and the lineage behind every value.
          </div>
        </div>

        {/* Where everything is, before any of it is filtered. Clicking a count
            opens the view it belongs to, so the numbers are navigation rather
            than decoration. */}
        <CountsStrip graphId={graphId} onOpen={setView} />

        {/* The entity picker sits above the sub-tabs, not inside one of them:
            all three views are the same pipeline seen at three stages, and
            switching stage should not lose the entity you were looking at. */}
        <div className="cp-recbar">
          <label className="small faint" htmlFor="records-entity">
            Entity
          </label>
          <select
            id="records-entity"
            className="select"
            style={{ maxWidth: 220 }}
            value={nodeTypeKey}
            onChange={(e) => {
              setNodeTypeKey(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All entities</option>
            {model.node_types.map((node) => (
              <option key={node.node_type_id} value={node.key}>
                {node.key}
              </option>
            ))}
          </select>
          <span className="cp-spacer" />
        </div>

        <SubTabs<View>
          value={view}
          onChange={setView}
          tabs={[
            { key: 'golden', label: 'Golden records' },
            { key: 'staged', label: 'Rejected records' },
            { key: 'review', label: 'Potential matches' },
          ]}
        />

        {view === 'golden' && (
          <>
            <div className="cp-recbar">
              <SearchBox
                value={query}
                placeholder={
                  records.data
                    ? `Search ${records.data.meta.total.toLocaleString()} records…`
                    : 'Search records…'
                }
                onChange={(next) => {
                  setQuery(next)
                  setPage(1)
                }}
              />
              <span className="cp-spacer" />
              {records.data && (
                <Chip>
                  {records.data.meta.total.toLocaleString()} record
                  {records.data.meta.total === 1 ? '' : 's'}
                </Chip>
              )}
            </div>

            {records.isLoading && <SkeletonRows rows={8} columns={5} label="Loading records…" />}

            {records.data && records.data.records.length === 0 && (
              <EmptyState title="No records yet">
                Attach a source and ingest some rows — golden records appear once the pipeline has
                run.
              </EmptyState>
            )}

            {records.data && records.data.records.length > 0 && (
              <>
                <div className="cp-card cp-tablewrap">
                  <table className="cp-table">
                    <thead>
                      <tr>
                        <th>Record</th>
                        {columns.map((column) => (
                          <th key={column.property_id}>{column.key}</th>
                        ))}
                        <th>Type</th>
                        <th>Sources</th>
                        <th>Quality</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.data.records.map((record) => (
                        <tr
                          key={record.entity_id}
                          data-clickable="true"
                          onClick={() => navigate(`/brain/${graphId}/records/${record.entity_id}`)}
                        >
                          <td style={{ fontWeight: 640 }}>
                            {record.label ?? record.entity_id.slice(0, 8)}
                          </td>
                          {columns.map((column) => (
                            <td key={column.property_id} className="mono small">
                              {formatValue(record.properties[column.key])}
                            </td>
                          ))}
                          <td className="muted">{record.node_type_key}</td>
                          <td>
                            {/* Which systems asserted this record — the reason it is one record
                                and not three. */}
                            {record.sources?.length ? (
                              <span className="row-tight" style={{ flexWrap: 'wrap', gap: 4 }}>
                                {[
                                  ...new Set(
                                    record.sources.map((s) => s.source_id).filter(Boolean),
                                  ),
                                ].map((id) => (
                                  <span key={id} className="flag">
                                    {id}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="faint small">—</span>
                            )}
                          </td>
                          <td className="num">
                            {record.quality_score === null
                              ? '—'
                              : `${Math.round(record.quality_score * 100)}%`}
                          </td>
                          <td>
                            <Chip tone={STATUS_TONE[record.status] ?? 'plain'}>
                              {record.status}
                            </Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {records.data.meta.total > records.data.meta.per_page && (
                  <div className="row">
                    <button
                      className="btn btn-sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </button>
                    <span className="small muted">
                      Page {records.data.meta.page} of{' '}
                      {Math.ceil(records.data.meta.total / records.data.meta.per_page)}
                    </span>
                    <button
                      className="btn btn-sm"
                      disabled={page * records.data.meta.per_page >= records.data.meta.total}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {view === 'staged' && (
          <StagedRecords graphId={graphId} model={model} nodeTypeKey={nodeTypeKey} />
        )}
        {view === 'review' && (
          <ReviewQueue graphId={graphId} model={model} nodeTypeKey={nodeTypeKey} />
        )}
      </div>
    </div>
  )
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
